import { z } from 'zod';
import { WORK_ORDER_PRIORITIES, WORK_ORDER_TYPES } from './types';

/**
 * Two distinct schemas — same split-pattern as Equipment / EquipmentType:
 *
 *   workOrderCreateSchema  full payload, used by the create form.
 *   workOrderUpdateSchema  editable fields only — title, description,
 *                          priority, planning, estimatedHours.
 *
 * Server-immutable on the WO body:
 *   - number             auto-generated, never written from the client
 *   - type               cannot change after creation (reporting bucket)
 *   - status             changes only through /transition (state machine)
 *   - equipmentId        identity, can't be reassigned
 *   - vesselId/companyId denormalized from equipment
 *   - maintenancePlanId  set on auto-generation, immutable
 *   - createdByUserId    auto-filled on creation
 *
 * Lifecycle timestamps and signature fields are filled by the backend
 * as the state machine progresses, never by the client form.
 *
 * Dates flow as `YYYY-MM-DD` strings (HTML <input type="date">).
 * estimatedHours is a string in the form too, since the backend uses
 * Decimal(6,2) and we forward the string verbatim.
 */

const optionalString = (max: number) =>
  z
    .string()
    .max(max)
    .optional()
    .transform((v) => (v === undefined || v === '' ? null : v));

const optionalDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD')
  .or(z.literal(''))
  .optional()
  .transform((v) => (v === undefined || v === '' ? null : v));

const optionalDecimal = z
  .string()
  .regex(/^\d{1,4}(\.\d{1,2})?$/, 'Decimal up to 9999.99')
  .or(z.literal(''))
  .optional()
  .transform((v) => (v === undefined || v === '' ? null : v));

const optionalUuid = z
  .string()
  .uuid()
  .or(z.literal(''))
  .optional()
  .transform((v) => (v === undefined || v === '' ? null : v));

/** Editable-anywhere fields (both create and update). */
const editableFields = {
  title: z.string().min(1).max(500),
  description: optionalString(10000),
  priority: z.enum(WORK_ORDER_PRIORITIES).default('normal'),
  plannedStartAt: optionalDate,
  dueAt: optionalDate,
  estimatedHours: optionalDecimal,
};

export const workOrderCreateSchema = z.object({
  ...editableFields,
  type: z.enum(WORK_ORDER_TYPES),
  equipmentId: z.string().uuid(),
  maintenancePlanId: optionalUuid,
});

export type WorkOrderCreateValues = z.infer<typeof workOrderCreateSchema>;

export const workOrderUpdateSchema = z.object({
  ...editableFields,
});

export type WorkOrderUpdateValues = z.infer<typeof workOrderUpdateSchema>;
